import React from 'react';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';

import { VerificationCredits } from 'api/bitedb/entities/bulkVerificationRun.service';
import { formatCount, plural, widthPercent } from './bulkVerificationFormat';

interface BulkVerificationCreditsProps {
  creditsRequired: number;
  creditsRemaining: number;
  /** Null when the team's allowance could not be read. */
  credits: VerificationCredits | null;
  onChangeSelection: () => void;
  onViewCreditPlans: () => void;
  now?: Date;
}

const utcMonthName = (date: Date): string =>
  date.toLocaleString('en-US', { month: 'long', timeZone: 'UTC' });

const formatResetDate = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });

/** The credits meter on Review & confirm: used, this run, and what is left. */
const BulkVerificationCredits: React.FC<BulkVerificationCreditsProps> = ({
  creditsRequired,
  creditsRemaining,
  credits,
  onChangeSelection,
  onViewCreditPlans,
  now = new Date(),
}) => {
  const used = credits?.usedThisMonth ?? 0;
  const shortBy = Math.max(0, creditsRequired - creditsRemaining);
  const scale = Math.max(
    credits?.allowance ?? used + creditsRemaining,
    used + creditsRequired,
  );

  return (
    <section className='bdb-bulk-verify-box' aria-label='Verification credits'>
      <div className='bdb-bulk-verify-label'>
        Verification credits · {utcMonthName(now)}
      </div>
      <div className='bdb-bulk-verify-credit-big'>
        <span className='bdb-bulk-verify-verdict'>
          {formatCount(creditsRemaining)}
        </span>
        <span className='bdb-bulk-verify-muted'>
          {credits
            ? `left of ${formatCount(credits.allowance)} this month`
            : 'left this month'}
        </span>
      </div>
      <div className='bdb-bulk-verify-meter' aria-hidden='true'>
        <i
          className='bdb-bulk-verify-meter-used'
          style={{ width: widthPercent(used, scale) }}
        />
        <i
          className='bdb-bulk-verify-meter-run'
          style={{
            width: widthPercent(
              Math.min(creditsRequired, creditsRemaining),
              scale,
            ),
          }}
        />
        {shortBy > 0 && (
          <i
            className='bdb-bulk-verify-meter-short'
            style={{ width: widthPercent(shortBy, scale) }}
          />
        )}
      </div>
      <dl className='bdb-bulk-verify-legend'>
        {credits && (
          <div>
            <dt>
              <span className='bdb-bulk-verify-swatch bdb-bulk-verify-meter-used' />
              Used this month
            </dt>
            <dd>{formatCount(used)}</dd>
          </div>
        )}
        <div>
          <dt>
            <span className='bdb-bulk-verify-swatch bdb-bulk-verify-meter-run' />
            This run
          </dt>
          <dd>{formatCount(creditsRequired)}</dd>
        </div>
        {shortBy > 0 ? (
          <div className='bdb-bulk-verify-legend-total'>
            <dt>
              <span className='bdb-bulk-verify-swatch bdb-bulk-verify-meter-short' />
              Short by
            </dt>
            <dd className='bdb-bulk-verify-short-value'>
              {formatCount(shortBy)}
            </dd>
          </div>
        ) : (
          <div className='bdb-bulk-verify-legend-total'>
            <dt>
              <span className='bdb-bulk-verify-swatch' />
              Left after this run
            </dt>
            <dd>{formatCount(creditsRemaining - creditsRequired)}</dd>
          </div>
        )}
      </dl>
      {shortBy > 0 && (
        <div className='bdb-bulk-verify-short' role='alert'>
          <div className='bdb-bulk-verify-short-head'>
            <WarningAmberOutlinedIcon
              className='bdb-bulk-verify-short-icon'
              aria-hidden='true'
            />
            <span>
              This run needs {formatCount(creditsRequired)}{' '}
              {plural(creditsRequired, 'credit', 'credits')}. Your team has{' '}
              {formatCount(creditsRemaining)} left this month.
            </span>
          </div>
          <p className='bdb-bulk-verify-short-text'>
            A run reserves every credit it needs before it starts, so it
            can&apos;t run part of the list.{' '}
            {creditsRemaining > 0
              ? `Narrow it to ${formatCount(
                  creditsRemaining,
                )} or fewer entities with details to verify, or add credits.`
              : 'Add credits, or wait for the monthly reset.'}
          </p>
          <div className='bdb-bulk-verify-short-actions'>
            <button
              type='button'
              className='btn-outline bdb-bulk-verify-btn-sm'
              onClick={onChangeSelection}
            >
              Change selection
            </button>
            <button
              type='button'
              className='bdb-bulk-verify-link'
              onClick={onViewCreditPlans}
            >
              View credit plans
            </button>
          </div>
        </div>
      )}
      {credits?.periodEnd && (
        <div className='bdb-bulk-verify-reset'>
          Credits reset on {formatResetDate(credits.periodEnd)} (UTC).
        </div>
      )}
    </section>
  );
};

export default BulkVerificationCredits;
